/**
 * Wave 95 — Housy proactive bubble.
 * Wave 95.2 — fallback synthesis from /v1/agent/digest.
 * Wave 380 — UI polish cluster (2026-05-08):
 *   FIX 1 · BUBBLE: SÓ PRA URGENT — Emil principle, "tens-of-times/day → reduce."
 *           synthesizeFromDigest now returns null unless `anomalies.block > 0`.
 *           Warm/info totals continue to drive the badge count via Housy
 *           avatar's `unreadCount`, but they no longer pop a bubble.
 *           Auto-dismiss reduced 8s → 6s (Emil sweet spot for read+decide).
 *           Re-show throttle now persists in sessionStorage by (kind,
 *           signature) for 30 min, so F5 doesn't reset the dismissal.
 *   FIX 2 · BUBBLE COLADA NO HOUSY — Emil "transform-origin matters."
 *           Bubble no longer self-positions at fixed bottom-24 right-6;
 *           the parent (HousyFloater) places it as an absolute overlay
 *           anchored at TOP of the avatar with an 8 px gap. A small CSS
 *           triangle (clip-path) points down at the avatar so the bubble
 *           reads as "coming out of" Housy. Spring-easeout enter, snappy
 *           ease-out exit (180 ms). transform-origin set to bottom center.
 *           Max-width capped at 240 px; body line-clamps to 2 lines with
 *           ellipsis so a long synthesis doesn't blow out the layout.
 *
 * A tiny floating speech-bubble glued to Housy. Polls /v1/me/nudges
 * every 60 s, picks the highest-severity pending row, and surfaces it
 * as a balloon with one line of text and a tail pointing back at the
 * mark. Auto-dismisses after 6 s if the user doesn't touch it; click →
 * opens the Housy panel and marks the nudge shown.
 *
 * Synthetic fallback only fires when no real nudges are queued AND the
 * digest has at least one *blocking* anomaly. This is the
 * "tens-of-times/day → reduce" rule from Emil: a bubble is interruption
 * theatre and we earn the right to use it only for the most urgent row.
 *
 * Stays out of the way:
 *   • Severity drives the tone (info/warm/warn/urgent → palette)
 *   • Only one bubble at a time; rest queue
 *   • Hidden if the chat panel is already open
 *   • Pauses while the tab is in the background
 *   • Synthetic fallback only fires for `block > 0`, never warm/info,
 *     which both leaves the user alone *and* keeps the badge count as
 *     the persistent ambient cue.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth.store';
import type { Digest } from '@/hooks/useDigest';
import { useT } from '@/hooks/useT';

interface Nudge {
  id: string;
  kind: string;
  body: string;
  context_json: string;
  severity: 'info' | 'warm' | 'warn' | 'urgent';
  created_at: string;
}

// Wave 380 · sessionStorage-backed dismissal throttle. Keyed by (kind,
// signature) so two distinct urgent synths don't share a dismiss; lives
// for 30 min and survives F5 (per JP, in-memory `synthDismissed` reset
// on every reload was the main offender).
const DISMISS_KEY_PREFIX = 'builder.housy.bubble.dismissed.';
const DISMISS_TTL_MS = 30 * 60 * 1000; // 30 min
function dismissKey(n: Nudge): string {
  // For synthetics, the body is the signature (counts already encoded);
  // for real nudges, the server id is unique.
  return n.id.startsWith('synthetic:')
    ? `synth.${n.kind}.${n.body}`
    : `nudge.${n.id}`;
}
function dismissedRecently(n: Nudge): boolean {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY_PREFIX + dismissKey(n));
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_MS;
  } catch {
    return false;
  }
}
function rememberDismiss(n: Nudge): void {
  try {
    sessionStorage.setItem(DISMISS_KEY_PREFIX + dismissKey(n), String(Date.now()));
  } catch {
    // ignore (private browsing / quota)
  }
}

/**
 * Build a synthetic "you have stuff waiting" nudge from the digest.
 *
 * Wave 380 · FIX 1: this now returns null unless we have a blocking
 * (urgent) anomaly. Warm/info totals still update the avatar badge via
 * `unreadCount`, but we don't pop a bubble for them — JP's screenshot
 * feedback ("tá chata, só pra muito urgente") was the trigger.
 */
function synthesizeFromDigest(
  d: Digest | null,
  pendingUsers: number,
  isEn: boolean,
): Nudge | null {
  if (!d) return null;
  const block = d.anomalies?.block ?? 0;
  // Hard gate: synthetic bubble fires only when severity calculates to
  // 'urgent'. With no real nudges queued and no blocking anomalies, the
  // ambient badge does the work; we stay quiet.
  if (block <= 0) return null;

  const parts: string[] = [];
  const totalAnoms = d.anomalies?.total ?? 0;
  if (totalAnoms > 0) {
    parts.push(isEn
      ? `${totalAnoms} issue${totalAnoms > 1 ? 's' : ''}`
      : `${totalAnoms} anomalia${totalAnoms > 1 ? 's' : ''}`);
  }
  if (d.rncsAssigned > 0) {
    parts.push(isEn
      ? `${d.rncsAssigned} NCR${d.rncsAssigned > 1 ? 's' : ''} for you`
      : `${d.rncsAssigned} RNC${d.rncsAssigned > 1 ? 's' : ''} pra você`);
  }
  if (d.pendingApprovals > 0) {
    parts.push(isEn
      ? `${d.pendingApprovals} approval${d.pendingApprovals > 1 ? 's' : ''}`
      : `${d.pendingApprovals} aprovaç${d.pendingApprovals > 1 ? 'ões' : 'ão'}`);
  }
  if (d.overdueMedicoes > 0) {
    parts.push(isEn
      ? `${d.overdueMedicoes} overdue measurement${d.overdueMedicoes > 1 ? 's' : ''}`
      : `${d.overdueMedicoes} medição${d.overdueMedicoes > 1 ? 'ões' : ''} atrasada${d.overdueMedicoes > 1 ? 's' : ''}`);
  }
  if (pendingUsers > 0) {
    parts.push(isEn
      ? `${pendingUsers} registration${pendingUsers > 1 ? 's' : ''} to approve`
      : `${pendingUsers} cadastro${pendingUsers > 1 ? 's' : ''} pra aprovar`);
  }
  // No descriptive parts? Fall back to a one-liner about anomalies; the
  // gate above already proved we have a blocker.
  if (parts.length === 0) {
    parts.push(isEn
      ? `${block} blocking item${block > 1 ? 's' : ''}`
      : `${block} item${block > 1 ? 'ns' : ''} bloqueando`);
  }

  // First two parts as a comma list, then "+N" if there are more.
  const head = parts.slice(0, 2).join(', ');
  const rest = parts.length > 2 ? ` +${parts.length - 2}` : '';
  const body = isEn
    ? `You have ${head}${rest} waiting.`
    : `Você tem ${head}${rest} esperando.`;

  return {
    id: `synthetic:${body}`,
    kind: 'fyi.pending',
    body,
    context_json: JSON.stringify({ source: 'digest_fallback', urgent: true }),
    severity: 'urgent',
    created_at: new Date().toISOString(),
  };
}

/**
 * HousyBubble.
 *
 * Wave 380 · FIX 2: this component no longer owns its on-screen anchor.
 * It renders an `absolute` overlay positioned by its parent
 * (`HousyFloater`) — anchored to the TOP of the avatar with an 8 px gap
 * and a small triangle pointing down at it. The previous self-positioned
 * `fixed bottom-24 right-6` wrapper plus the localStorage offset poll
 * (Wave 332 hack) are both gone — we share the parent's drag transform
 * the proper way.
 */
export function HousyBubble({
  chatOpen,
  onOpenChat,
  digest,
  pendingUsers = 0,
}: {
  chatOpen: boolean;
  onOpenChat: () => void;
  digest?: Digest | null;
  pendingUsers?: number;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const token = useAuth((s) => s.token);
  const reduceMotion = useReducedMotion() ?? false;
  const [queue, setQueue] = useState<Nudge[]>([]);
  const [active, setActive] = useState<Nudge | null>(null);
  // Tick that re-evaluates `dismissedRecently` after a dismissal. We
  // don't need to read sessionStorage on every render; flipping this
  // forces the selection effect below to re-run and skip the dismissed
  // entry next round.
  const [, setDismissTick] = useState(0);

  // Poll for nudges. 60s default, slightly faster after an action so
  // the next one in the queue surfaces while the user is still here.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const pull = async () => {
      if (cancelled || document.hidden) return;
      try {
        const rows = await api.listNudges();
        if (!cancelled) setQueue(rows);
      } catch {
        // ignore
      }
    };
    pull();
    const iv = window.setInterval(pull, 60_000);
    const onVis = () => { if (!document.hidden) pull(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token]);

  // Build the synthetic bubble lazily from the digest. Only used when
  // the real queue is empty — otherwise persona/anomaly nudges win.
  const synthetic = useMemo(
    () => synthesizeFromDigest(digest ?? null, pendingUsers, isEn),
    [digest, pendingUsers, isEn],
  );

  // Pop the next bubble when nothing is showing. Only emits 'urgent'
  // nudges from the queue — warm/info real nudges stay quiet too,
  // matching the synthetic gate. (Server may still send them; we just
  // keep them in the queue without surfacing.)
  useEffect(() => {
    if (chatOpen) return; // hide while the panel is open
    if (active) return;
    // Skip queued non-urgent rows AND anything already dismissed within
    // the throttle window. The first urgent, undismissed row wins.
    const next = queue.find(
      (n) => n.severity === 'urgent' && !dismissedRecently(n),
    );
    if (next) {
      setActive(next);
      // Real nudges round-trip to the server.
      api.markNudgeSeen(next.id).catch(() => { /* ignore */ });
      return;
    }
    if (synthetic && !dismissedRecently(synthetic)) {
      setActive(synthetic);
    }
  }, [queue, active, chatOpen, synthetic]);

  // Auto-dismiss after 6 s if the user doesn't engage. Wave 380 · FIX 1:
  // 8 s → 6 s. Long enough to read+decide on a one-liner; short enough
  // not to linger after the user's eye has moved on.
  useEffect(() => {
    if (!active) return;
    const t = window.setTimeout(() => {
      const dismissed = active;
      setActive(null);
      rememberDismiss(dismissed);
      if (!dismissed.id.startsWith('synthetic:')) {
        setQueue((q) => q.filter((n) => n.id !== dismissed.id));
      }
      setDismissTick((n) => n + 1);
    }, 6000);
    return () => window.clearTimeout(t);
  }, [active]);

  if (chatOpen) return null;

  const tone = active?.severity ?? 'info';
  const palette = {
    info:   { bg: 'bg-surface-2',     text: 'text-ink',         dot: 'bg-ink-subtle', tail: 'rgb(var(--surface-2))' },
    warm:   { bg: 'bg-amber-500/12',  text: 'text-amber-100',   dot: 'bg-amber-300',  tail: 'rgba(245,158,11,0.12)' },
    warn:   { bg: 'bg-yellow-500/14', text: 'text-yellow-100',  dot: 'bg-yellow-300', tail: 'rgba(234,179,8,0.14)' },
    urgent: { bg: 'bg-red-500/14',    text: 'text-red-100',     dot: 'bg-red-300',    tail: 'rgba(239,68,68,0.14)' },
  }[tone];

  // Wave 380 · FIX 2 — anchor wrapper. Bubble sits *above* the avatar
  // (parent container is HousyFloater, which itself is positioned at
  // bottom-right and follows the drag offset). The avatar is 72 px,
  // `bottom: 80` = 72 + 8 px gap. `left: 36` = avatar center; the
  // motion.button uses `transform: translateX(-50%)` to center.
  // Pointer-events on the wrapper are off (it's wider than the bubble);
  // the button itself opts back in.
  return (
    <div
      className="absolute pointer-events-none z-[1]"
      style={{ bottom: 80, left: 36 }}
    >
      <AnimatePresence>
        {active && (
          <motion.button
            key={active.id}
            type="button"
            onClick={() => {
              const dismissed = active;
              rememberDismiss(dismissed);
              if (!dismissed.id.startsWith('synthetic:')) {
                api.dismissNudge(dismissed.id).catch(() => { /* ignore */ });
                setQueue((q) => q.filter((n) => n.id !== dismissed.id));
              }
              setActive(null);
              setDismissTick((n) => n + 1);
              onOpenChat();
            }}
            // Wave 380 · FIX 2 · spring enter, snappy exit, anchored
            // transform-origin at bottom center so the bubble appears
            // to grow out of the top of Housy.
            initial={
              reduceMotion
                ? { opacity: 0, x: '-50%' }
                : { opacity: 0, y: 6, scale: 0.92, x: '-50%' }
            }
            animate={
              reduceMotion
                ? { opacity: 1, x: '-50%' }
                : { opacity: 1, y: 0, scale: 1, x: '-50%' }
            }
            exit={
              reduceMotion
                ? { opacity: 0, x: '-50%' }
                : { opacity: 0, y: 4, scale: 0.96, x: '-50%' }
            }
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    // Enter springs in; exit eases out 180 ms (asymmetric).
                    type: 'spring',
                    duration: 0.45,
                    bounce: 0.18,
                  }
            }
            style={{ transformOrigin: 'bottom center' }}
            className={`pointer-events-auto relative block max-w-[240px] rounded-2xl border border-line ${palette.bg} ${palette.text} shadow-elev-3 px-3 py-2 text-left text-[13px] leading-snug pressable backdrop-blur`}
          >
            <span className={`absolute left-2 top-2 inline-block h-1.5 w-1.5 rounded-full ${palette.dot}`} aria-hidden />
            {/* Body — 2-line clamp with ellipsis. Tailwind utilities
                cover line-clamp-2 since v3.3; the inline -webkit fallback
                is kept for older Safari which still ships in builds.web. */}
            <span
              className="block pl-3"
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {active.body}
            </span>
            {/* Tail (Wave 380 · FIX 2) — 8×6 px triangle pointing DOWN
                at the avatar. clip-path keeps it crisp on retina; matches
                bubble bg color via inline style for perfect tone match. */}
            <span
              aria-hidden
              className="absolute left-1/2 -translate-x-1/2"
              style={{
                bottom: -6,
                width: 8,
                height: 6,
                clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
                background: palette.tail,
              }}
            />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
